<main class="tfr-page-shell-narrow py-5 lg:py-10">

  <!-- ── Hero header ──────────────────────────────────────────────── -->
  <header class="flex items-start gap-4 mb-8">
    <div class="h-16 w-16 sm:h-20 sm:w-20 rounded-full overflow-hidden ring-2 ring-pale-gray shrink-0 bg-pale-gray">
      @if (displayPhotoUrl(); as photoUrl) {
        <img [src]="photoUrl" [alt]="profileForm.controls.displayName.value || profile()?.displayName || 'Tu foto'"
             class="w-full h-full object-cover" />
      } @else {
        <div class="w-full h-full flex items-center justify-center text-[20px] sm:text-[22px] font-semibold text-slate-blue">
          {{ avatarInitials() }}
        </div>
      }
    </div>
    <div class="min-w-0 flex-1 pt-1">
      <h1 class="text-[20px] sm:text-[24px] font-bold tracking-tight text-midnight-indigo truncate">
        {{ profileForm.controls.displayName.value || profile()?.displayName || 'Sin nombre' }}
      </h1>
      <p class="text-[13px] text-slate-blue truncate">{{ profile()?.email }}</p>
      <div class="flex items-center flex-wrap gap-x-2 gap-y-1 mt-2">
        @if (roleLabel()) {
          <span class="inline-flex items-center px-2 py-0.5 rounded-full bg-pale-gray text-[11px] font-semibold text-slate-blue">
            {{ roleLabel() }}
          </span>
        }
        @if (isOwner() && ownedAssetCount() !== null) {
          <span class="text-[11px] text-steel-gray">·</span>
          <span class="text-[11px] text-slate-blue">
            {{ ownedAssetCount() }} {{ ownedAssetCount() === 1 ? 'espacio' : 'espacios' }}
          </span>
        }
      </div>
    </div>
  </header>

  <div class="space-y-6">

    <!-- ── Tu perfil (todos los roles) ────────────────────────────── -->
    <section class="tfr-settings-card">
      <div class="flex items-start gap-3 mb-4">
        <div class="h-9 w-9 rounded-lg bg-pale-gray flex items-center justify-center shrink-0">
          <lucide-icon name="user" class="h-4 w-4 text-action-blue" />
        </div>
        <div class="min-w-0 flex-1">
          <h2 class="text-[15px] font-semibold text-midnight-indigo">Tu perfil</h2>
          <p class="text-[12px] text-steel-gray">
            Nombre y foto visibles en la app y en tus reservas.
          </p>
        </div>
      </div>

      <form [formGroup]="profileForm" (ngSubmit)="saveProfile()" class="space-y-4" novalidate>
        <div class="flex flex-col sm:flex-row sm:items-start gap-4">
          <div class="flex flex-col items-center sm:items-start gap-2 shrink-0">
            <div class="h-20 w-20 rounded-full overflow-hidden ring-2 ring-pale-gray bg-pale-gray">
              @if (displayPhotoUrl(); as photoUrl) {
                <img [src]="photoUrl"
                     [alt]="profileForm.controls.displayName.value || 'Tu foto'"
                     class="w-full h-full object-cover" />
              } @else {
                <div class="w-full h-full flex items-center justify-center text-[22px] font-semibold text-slate-blue">
                  {{ avatarInitials() }}
                </div>
              }
            </div>
            <label class="cursor-pointer">
              <input type="file"
                     accept="image/jpeg,image/png,image/webp,image/gif"
                     class="sr-only"
                     (change)="onPhotoSelected($event)" />
              <span class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-pale-gray text-[12px] font-semibold text-slate-blue hover:bg-cloud-mist transition-colors">
                <lucide-icon name="camera" class="h-3.5 w-3.5" />
                Cambiar foto
              </span>
            </label>
            <p class="text-[11px] text-steel-gray text-center sm:text-left">JPG, PNG o WebP · m&aacute;x. 2 MB</p>
          </div>

          <div class="flex-1 min-w-0 flex flex-col gap-1">
            <label class="tfr-field-label" for="profile-display-name">Nombre</label>
            <input id="profile-display-name"
                   type="text"
                   formControlName="displayName"
                   autocomplete="name"
                   maxlength="80"
                   placeholder="Tu nombre"
                   class="tfr-input bg-cloud-mist" />
            @if (profileForm.controls.displayName.touched && profileForm.controls.displayName.hasError('required')) {
              <p class="tfr-error">Escribe tu nombre.</p>
            }
            @if (profileForm.controls.displayName.touched && profileForm.controls.displayName.hasError('minlength')) {
              <p class="tfr-error">M&iacute;nimo 2 caracteres.</p>
            }
          </div>
        </div>

        @if (profileError()) {
          <p class="text-[12px] text-red-600">{{ profileError() }}</p>
        } @else if (profileSuccess()) {
          <p class="text-[12px] text-emerald-600">Perfil actualizado correctamente.</p>
        }

        <div class="flex justify-end pt-1">
          <button type="submit"
                  [disabled]="savingProfile() || !profileDirty()"
                  class="tfr-btn-primary px-5 py-2.5 text-[13px]">
            @if (savingProfile()) { Guardando&hellip; } @else { Guardar perfil }
          </button>
        </div>
      </form>
    </section>

    <!-- ── Datos de facturación (todos los roles) ─────────────────── -->
    <section class="tfr-settings-card">
      <div class="flex items-start gap-3 mb-4">
        <div class="h-9 w-9 rounded-lg bg-pale-gray flex items-center justify-center shrink-0">
          <lucide-icon name="file-text" class="h-4 w-4 text-action-blue" />
        </div>
        <div class="min-w-0 flex-1">
          <h2 class="text-[15px] font-semibold text-midnight-indigo">Datos de facturaci&oacute;n</h2>
          <p class="text-[12px] text-steel-gray">
            @if (isOwner()) {
              Obligatorios para que TimeFlexRent pueda facturarte su comisi&oacute;n.
            } @else {
              Opcionales; sirven para reutilizar tus datos en contratos de reserva.
            }
          </p>
        </div>
        @if (billingProfileComplete() === true) {
          <span class="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-[11px] font-semibold text-emerald-700">
            <lucide-icon name="check" class="h-3 w-3" />
            Completo
          </span>
        } @else if (billingProfileComplete() === false) {
          <span class="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-[11px] font-semibold text-amber-800">
            Pendiente
          </span>
        }
      </div>

      <form [formGroup]="billingForm" (ngSubmit)="saveBilling()" class="space-y-4" novalidate>
        <fieldset class="space-y-2">
          <legend class="tfr-field-label">Tipo de titular</legend>
          <div class="flex flex-col sm:flex-row gap-3">
            @for (opt of billingEntityTypeOptions; track opt.value) {
              <label class="flex items-center gap-2 cursor-pointer text-[13px] text-midnight-indigo">
                <input type="radio" formControlName="entityType" [value]="opt.value" />
                {{ opt.label }}
              </label>
            }
          </div>
        </fieldset>

        @if (billingForm.controls.entityType.value === 'persona_fisica') {
          <label class="flex items-start gap-2 rounded-xl border border-pale-gray bg-cloud-mist px-3 py-3
                        cursor-pointer text-[13px] text-midnight-indigo">
            <input type="checkbox" formControlName="actsAsBusiness" class="mt-0.5" />
            <span>
              Act&uacute;o como empresario o profesional en esta operaci&oacute;n
              <span class="mt-0.5 block text-[11px] text-steel-gray">
                Permite determinar si debes practicar retenci&oacute;n cuando TimeFlexRent facture como aut&oacute;nomo.
              </span>
            </span>
          </label>
        }

        <div class="flex flex-col gap-1">
          <label class="tfr-field-label" for="billing-legal-name">Raz&oacute;n social / nombre fiscal</label>
          <input id="billing-legal-name" type="text" formControlName="legalName" autocomplete="organization"
                 class="tfr-input" />
          @if (showBillingError('legalName')) {
            <p class="tfr-error">{{ billingFieldError('legalName') }}</p>
          }
          @if (isOwner() && paymentsForm.controls.accountHolder.value) {
            <button type="button" (click)="useBankAccountHolderForBilling()"
                    class="self-start text-[11px] font-semibold text-action-blue hover:underline">
              Usar titular bancario (SEPA)
            </button>
          }
        </div>

        <div class="flex flex-col gap-1">
          <label class="tfr-field-label" for="billing-tax-id">CIF / NIF</label>
          <input id="billing-tax-id" type="text" formControlName="taxId" autocomplete="off"
                 class="tfr-input uppercase" />
          <p class="text-[11px] text-steel-gray">
            @if (billingForm.controls.entityType.value === 'persona_fisica') {
              DNI o NIE del titular.
            } @else {
              CIF de la sociedad o entidad.
            }
          </p>
          @if (showBillingError('taxId')) {
            <p class="tfr-error">{{ billingFieldError('taxId') }}</p>
          }
        </div>

        <div class="flex flex-col gap-1">
          <label class="tfr-field-label" for="billing-address">Direcci&oacute;n fiscal</label>
          <input id="billing-address" type="text" formControlName="addressLine1" autocomplete="street-address"
                 placeholder="Calle, n&uacute;mero, piso" class="tfr-input" />
          @if (showBillingError('addressLine1')) {
            <p class="tfr-error">{{ billingFieldError('addressLine1') }}</p>
          }
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div class="flex flex-col gap-1">
            <label class="tfr-field-label" for="billing-postal">C.P.</label>
            <input id="billing-postal" type="text" formControlName="postalCode" inputmode="numeric"
                   maxlength="5" class="tfr-input" />
            @if (showBillingError('postalCode')) {
              <p class="tfr-error">{{ billingFieldError('postalCode') }}</p>
            }
          </div>
          <div class="flex flex-col gap-1">
            <label class="tfr-field-label" for="billing-city">Ciudad</label>
            <input id="billing-city" type="text" formControlName="city" class="tfr-input" />
            @if (showBillingError('city')) {
              <p class="tfr-error">{{ billingFieldError('city') }}</p>
            }
          </div>
          <div class="flex flex-col gap-1">
            <label class="tfr-field-label" for="billing-province">Provincia</label>
            <input id="billing-province" type="text" formControlName="province" class="tfr-input" />
            @if (showBillingError('province')) {
              <p class="tfr-error">{{ billingFieldError('province') }}</p>
            }
          </div>
        </div>

        @if (billingError()) {
          <p class="text-[12px] text-red-600">{{ billingError() }}</p>
        } @else if (billingSuccess()) {
          <p class="text-[12px] text-emerald-600">Datos de facturaci&oacute;n guardados.</p>
        }

        <div class="flex justify-end pt-1">
          <button type="submit"
                  [disabled]="savingBilling() || billingForm.pristine"
                  class="tfr-btn-primary px-5 py-2.5 text-[13px]">
            @if (savingBilling()) { Guardando&hellip; } @else { Guardar facturaci&oacute;n }
          </button>
        </div>
      </form>
    </section>

    <!-- ── Perfil público (owner/admin only) ──────────────────────── -->
    @if (isOwner()) {
      <section class="tfr-settings-card">
        <div class="flex items-start gap-3 mb-4">
          <div class="h-9 w-9 rounded-lg bg-pale-gray flex items-center justify-center shrink-0">
            <lucide-icon name="globe" class="h-4 w-4 text-action-blue" />
          </div>
          <div class="min-w-0 flex-1">
            <h2 class="text-[15px] font-semibold text-midnight-indigo">Perfil p&uacute;blico</h2>
            <p class="text-[12px] text-steel-gray">
              Tu URL en TimeFlexRent. Usa solo min&uacute;sculas, n&uacute;meros y guiones.
            </p>
          </div>
        </div>

        @if (publicProfileUrl()) {
          <div class="flex items-center gap-2 mb-4 px-3 py-2 rounded-lg bg-cloud-mist border border-pale-gray">
            <lucide-icon name="external-link" class="h-3.5 w-3.5 text-steel-gray shrink-0" />
            <a [href]="publicProfileUrl()" target="_blank" rel="noopener"
               class="text-[12px] font-mono truncate flex-1 text-action-blue hover:underline">
              {{ publicProfileUrl() }}
            </a>
            <button type="button" (click)="copyPublicUrl()"
              class="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium text-slate-blue hover:bg-pale-gray transition-colors">
              <lucide-icon [name]="slugCopied() ? 'check' : 'copy'" class="h-3 w-3" />
              {{ slugCopied() ? 'Copiado' : 'Copiar' }}
            </button>
          </div>
        }

        <form [formGroup]="slugForm" (ngSubmit)="saveSlug()" class="space-y-3" novalidate>
          <div class="flex flex-col gap-1">
            <label class="tfr-field-label">Slug del propietario</label>
            <div class="flex items-stretch rounded-lg overflow-hidden border border-pale-gray bg-cloud-mist focus-within:border-action-blue focus-within:ring-2 focus-within:ring-action-blue/15 transition-colors">
              <span class="px-3 flex items-center text-[12px] text-steel-gray select-none whitespace-nowrap border-r border-pale-gray bg-snow-white/60">
                /propietario/
              </span>
              <input type="text"
                formControlName="slug"
                autocomplete="off"
                spellcheck="false"
                [attr.maxlength]="SLUG_MAX_LEN"
                placeholder="mi-nombre"
                class="flex-1 min-w-0 bg-transparent border-0 outline-none px-3 py-2.5 text-[13px] font-mono lowercase text-midnight-indigo placeholder:text-steel-gray/60" />
            </div>
            @if (slugError()) {
              <p class="tfr-error">{{ slugError() }}</p>
            } @else if (slugSuccess()) {
              <p class="text-[12px] text-emerald-600">Slug actualizado correctamente.</p>
            }
          </div>

          <div class="flex justify-end pt-1">
            <button type="submit"
              [disabled]="savingSlug() || slugForm.pristine"
              class="tfr-btn-primary px-5 py-2.5 text-[13px]">
              @if (savingSlug()) { Guardando&hellip; } @else { Guardar }
            </button>
          </div>
        </form>
      </section>
    }

    <!-- ── Rol de cuenta ──────────────────────────────────────────── -->
    <section class="tfr-settings-card">
      <div class="flex items-start gap-3 mb-4">
        <div class="h-9 w-9 rounded-lg bg-pale-gray flex items-center justify-center shrink-0">
          <lucide-icon name="user-cog" class="h-4 w-4 text-action-blue" />
        </div>
        <div class="min-w-0 flex-1">
          <h2 class="text-[15px] font-semibold text-midnight-indigo">Rol de cuenta</h2>
          <p class="text-[12px] text-steel-gray">Determina qu&eacute; funciones ves en el panel.</p>
        </div>
      </div>

      <div class="space-y-3">
        @for (r of roles; track r.value) {
          @let isActive = profile()?.role === r.value;
          @let isLocked = r.value === 'user' && switchBlocked();
          <button
            type="button"
            (click)="changeRole(r.value)"
            [disabled]="saving() || isActive"
            class="tfr-role-option"
            [class.tfr-role-option--active]="isActive"
            [class.tfr-role-option--idle]="!isActive">
            <div class="flex items-center justify-between gap-3">
              <div>
                <p class="text-[13px] font-semibold"
                   [class.text-action-blue]="isActive"
                   [class.text-midnight-indigo]="!isActive">
                  {{ r.label }}
                </p>
                <p class="text-[12px] mt-0.5 text-slate-blue">{{ r.description }}</p>
              </div>
              @if (isActive) {
                <span class="shrink-0 h-5 w-5 rounded-full flex items-center justify-center bg-action-blue">
                  <lucide-icon name="check" class="h-3 w-3 text-snow-white" />
                </span>
              } @else if (isLocked) {
                <span class="shrink-0 h-5 w-5 rounded-full border-2 flex items-center justify-center border-platinum-tint">
                  <lucide-icon name="lock" class="h-2.5 w-2.5 text-steel-gray" />
                </span>
              } @else {
                <span class="shrink-0 h-5 w-5 rounded-full border-2 border-pale-gray"></span>
              }
            </div>
          </button>
        }
      </div>

      @if (switchBlocked()) {
        <div class="tfr-callout mt-3">
          <lucide-icon name="info" class="shrink-0 mt-0.5 h-3.5 w-3.5 text-steel-gray" />
          <p class="text-[12px] leading-snug text-slate-blue">
            No puedes cambiar al rol de Arrendatario mientras tengas espacios creados (visibles u ocultos).
            Elimina todos tus espacios primero.
          </p>
        </div>
      }

      @if (saving()) {
        <p class="mt-3 text-[12px] text-center text-steel-gray">Guardando&hellip;</p>
      }
    </section>

    <!-- ── Avisos ─────────────────────────────────────────────────── -->
    <section class="tfr-settings-card">
      <div class="flex items-start gap-3 mb-4">
        <div class="h-9 w-9 rounded-lg bg-pale-gray flex items-center justify-center shrink-0">
          <lucide-icon name="bell" class="h-4 w-4 text-action-blue" />
        </div>
        <div class="min-w-0 flex-1">
          <h2 class="text-[15px] font-semibold text-midnight-indigo">Avisos</h2>
          <p class="text-[12px] text-steel-gray">
            D&oacute;nde te avisamos de cambios en tus reservas.
          </p>
        </div>
      </div>

      <div class="space-y-3">
        <div class="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-pale-gray bg-cloud-mist">
          <lucide-icon name="mail" class="h-4 w-4 shrink-0 text-steel-gray" />
          <div class="min-w-0 flex-1">
            <p class="text-[11px] uppercase tracking-wider font-semibold text-steel-gray">Correo</p>
            <p class="text-[13px] truncate text-midnight-indigo">{{ profile()?.email || '—' }}</p>
          </div>
        </div>

        <div class="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-dashed border-pale-gray bg-cloud-mist/40 opacity-75">
          <lucide-icon name="smartphone" class="h-4 w-4 shrink-0 text-steel-gray" />
          <div class="min-w-0 flex-1">
            <p class="text-[11px] uppercase tracking-wider font-semibold text-steel-gray">M&oacute;vil para SMS</p>
            <p class="text-[12px] text-slate-blue">Pronto &mdash; cuando integremos el servicio de mensajer&iacute;a.</p>
          </div>
        </div>
      </div>
    </section>

    @if (isOwner()) {
      <section class="tfr-settings-card mb-6">
        <div class="flex items-start gap-3 mb-4">
          <div class="h-9 w-9 rounded-lg bg-pale-gray flex items-center justify-center shrink-0">
            <lucide-icon name="wallet" class="h-4 w-4 text-action-blue" />
          </div>
          <div class="min-w-0 flex-1">
            <h2 class="text-[15px] font-semibold text-midnight-indigo">Recibe pagos con Stripe</h2>
            <p class="text-[12px] text-steel-gray">
              Conecta tu cuenta para cobrar el alquiler completo en la web (sin justificante manual del saldo).
            </p>
          </div>
        </div>

        <p class="text-[12px] font-medium text-midnight-indigo mb-1">
          Estado: <span class="text-action-blue">{{ stripeConnectLabel() }}</span>
        </p>
        @if (stripeConnectDetail(); as detail) {
          <p class="text-[11px] text-steel-gray leading-relaxed mb-3">{{ detail }}</p>
        } @else {
          <div class="mb-3"></div>
        }

        @if (stripeError()) {
          <p class="mb-3 text-[12px] text-red-700">{{ stripeError() }}</p>
        }

        <div class="flex flex-wrap gap-2 mb-4">
          <button type="button"
            (click)="startStripeOnboarding('account_onboarding')"
            [disabled]="stripeLoading() || stripeDisconnecting()"
            class="rounded-xl bg-midnight-indigo px-4 py-2.5 text-[12px] font-semibold text-white hover:brightness-110 disabled:opacity-50">
            @if (stripeConnectStatus() === 'none') {
              Conectar con Stripe
            } @else {
              Completar verificación
            }
          </button>
          @if (stripeConnectStatus() !== 'none') {
            <button type="button"
              (click)="refreshStripeConnectStatus()"
              [disabled]="stripeLoading() || stripeDisconnecting()"
              class="rounded-xl border border-pale-gray px-4 py-2.5 text-[12px] font-semibold text-midnight-indigo hover:bg-pale-gray/60 disabled:opacity-50">
              @if (stripeLoading()) {
                Actualizando&hellip;
              } @else {
                Actualizar estado
              }
            </button>
            <button type="button"
              (click)="startStripeOnboarding('account_update')"
              [disabled]="stripeLoading() || stripeDisconnecting()"
              class="rounded-xl border border-pale-gray px-4 py-2.5 text-[12px] font-semibold text-midnight-indigo hover:bg-pale-gray/60 disabled:opacity-50">
              Abrir formulario Stripe
            </button>
          }
          @if (canDisconnectStripe()) {
            <button type="button"
              (click)="disconnectStripe()"
              [disabled]="stripeLoading() || stripeDisconnecting()"
              class="rounded-xl border border-red-200 px-4 py-2.5 text-[12px] font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50">
              @if (stripeDisconnecting()) {
                Desvinculando&hellip;
              } @else {
                Desvincular Stripe
              }
            </button>
          }
        </div>

        @if (canEnableStripePayout()) {
          <div class="rounded-2xl border border-pale-gray bg-cloud-mist/60 p-4 space-y-2">
            <p class="text-[12px] font-semibold text-midnight-indigo">Modo de cobro activo</p>
            <label class="flex items-center gap-2 cursor-pointer">
              <input type="radio" name="payoutMode" [checked]="payoutMode() === 'STRIPE'"
                (change)="setPayoutMode('STRIPE')" [disabled]="payoutModeSaving()" />
              <span class="text-[13px]">Stripe — alquiler en Checkout</span>
            </label>
            <label class="flex items-center gap-2 cursor-pointer">
              <input type="radio" name="payoutMode" [checked]="payoutMode() === 'MANUAL'"
                (change)="setPayoutMode('MANUAL')" [disabled]="payoutModeSaving()" />
              <span class="text-[13px]">Manual — comisión online + saldo al propietario</span>
            </label>
          </div>
        }
      </section>
    }

    <!-- ── Cobros (owner/admin only) ──────────────────────────────── -->
    @if (isOwner()) {
      <section class="tfr-settings-card">
        <div class="flex items-start gap-3 mb-4">
          <div class="h-9 w-9 rounded-lg bg-pale-gray flex items-center justify-center shrink-0">
            <lucide-icon name="credit-card" class="h-4 w-4 text-action-blue" />
          </div>
          <div class="min-w-0 flex-1">
            <h2 class="text-[15px] font-semibold text-midnight-indigo">Cobros</h2>
            <p class="text-[12px] text-steel-gray">
              Activa los m&eacute;todos que aceptas para el alquiler y la fianza reembolsable de tus inquilinos.
            </p>
          </div>
        </div>

        <form [formGroup]="paymentsForm" (ngSubmit)="savePayments()" class="space-y-4" novalidate>

          <!-- SEPA -->
          <div class="rounded-2xl border border-pale-gray bg-cloud-mist/60 overflow-hidden">
            <label class="flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-cloud-mist transition-colors">
              <input type="checkbox" formControlName="sepaEnabled"
                class="mt-0.5 h-4 w-4 shrink-0 rounded border-pale-gray text-action-blue focus:ring-2 focus:ring-action-blue/30" />
              <div class="min-w-0 flex-1">
                <p class="text-[13px] font-semibold text-midnight-indigo">Transferencia (SEPA)</p>
                <p class="text-[11px] text-steel-gray">Tu inquilino te transfiere a tu IBAN.</p>
              </div>
            </label>

            @if (paymentsForm.controls.sepaEnabled.value) {
              <div class="px-4 pb-4 pt-2 space-y-3 border-t border-pale-gray bg-snow-white">
                <div class="flex flex-col gap-1">
                  <label class="tfr-field-label">Titular de la cuenta</label>
                  <input type="text" formControlName="accountHolder" placeholder="Nombre y apellidos del titular"
                    class="tfr-input bg-cloud-mist" />
                  @if (paymentsForm.controls['accountHolder'].touched && paymentsForm.controls['accountHolder'].hasError('required')) {
                    <p class="tfr-error">Obligatorio para recibir transferencias.</p>
                  }
                  @if (paymentsForm.controls['accountHolder'].touched && paymentsForm.controls['accountHolder'].hasError('minlength')) {
                    <p class="tfr-error">M&iacute;nimo 3 caracteres.</p>
                  }
                </div>

                <div class="flex flex-col gap-1">
                  <label class="tfr-field-label">IBAN</label>
                  <input type="text" formControlName="iban" placeholder="ES91 2100 0418 4502 0005 1332"
                    class="tfr-input bg-cloud-mist font-mono" />
                  @if (paymentsForm.controls['iban'].touched && paymentsForm.controls['iban'].hasError('required')) {
                    <p class="tfr-error">Obligatorio.</p>
                  }
                  @if (paymentsForm.controls['iban'].touched && paymentsForm.controls['iban'].hasError('pattern')) {
                    <p class="tfr-error">IBAN espa&ntilde;ol no v&aacute;lido (empieza por ES y 22 d&iacute;gitos).</p>
                  }
                </div>
              </div>
            }
          </div>

          <!-- Bizum -->
          <div class="rounded-2xl border border-pale-gray bg-cloud-mist/60 overflow-hidden">
            <label class="flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-cloud-mist transition-colors">
              <input type="checkbox" formControlName="bizumEnabled"
                class="mt-0.5 h-4 w-4 shrink-0 rounded border-pale-gray text-action-blue focus:ring-2 focus:ring-action-blue/30" />
              <div class="min-w-0 flex-1">
                <p class="text-[13px] font-semibold text-midnight-indigo">Bizum</p>
                <p class="text-[11px] text-steel-gray">Tu inquilino te env&iacute;a el pago al m&oacute;vil registrado en Bizum.</p>
              </div>
            </label>

            @if (paymentsForm.controls.bizumEnabled.value) {
              <div class="px-4 pb-4 pt-2 space-y-3 border-t border-pale-gray bg-snow-white">
                <div class="flex flex-col gap-1">
                  <label class="tfr-field-label">M&oacute;vil Bizum</label>
                  <input type="tel" formControlName="mobilePhone" placeholder="612 345 678"
                    class="tfr-input bg-cloud-mist" />
                  @if (paymentsForm.controls['mobilePhone'].touched && paymentsForm.controls['mobilePhone'].hasError('required')) {
                    <p class="tfr-error">Obligatorio.</p>
                  }
                  @if (paymentsForm.controls['mobilePhone'].touched && paymentsForm.controls['mobilePhone'].hasError('pattern')) {
                    <p class="tfr-error">M&oacute;vil espa&ntilde;ol no v&aacute;lido (6xx o 7xx, 9 d&iacute;gitos).</p>
                  }
                </div>
              </div>
            }
          </div>

          <!-- Efectivo -->
          <div class="rounded-2xl border border-pale-gray bg-cloud-mist/60 overflow-hidden">
            <label class="flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-cloud-mist transition-colors">
              <input type="checkbox" formControlName="cashEnabled"
                class="mt-0.5 h-4 w-4 shrink-0 rounded border-pale-gray text-action-blue focus:ring-2 focus:ring-action-blue/30" />
              <div class="min-w-0 flex-1">
                <p class="text-[13px] font-semibold text-midnight-indigo">Efectivo</p>
                <p class="text-[11px] text-steel-gray">
                  Tu inquilino te paga en mano el d&iacute;a del evento, en el local.
                </p>
              </div>
            </label>
          </div>

          @if (!paymentsForm.controls.sepaEnabled.value
                && !paymentsForm.controls.bizumEnabled.value
                && !paymentsForm.controls.cashEnabled.value) {
            <div class="tfr-callout">
              <lucide-icon name="info" class="shrink-0 mt-0.5 h-3.5 w-3.5 text-steel-gray" />
              @if (payoutMode() === 'STRIPE') {
                <p class="text-[12px] leading-snug text-slate-blue">
                  La fianza de tus inquilinos solo podr&aacute; gestionarse por retenci&oacute;n en tarjeta (Stripe).
                  Activa m&eacute;todos adicionales si quieres ofrecer m&aacute;s opciones.
                </p>
              } @else {
                <p class="text-[12px] leading-snug text-slate-blue">
                  Sin ning&uacute;n m&eacute;todo activo, los inquilinos no podr&aacute;n formalizar reservas en tus espacios.
                </p>
              }
            </div>
          }

          <div class="flex justify-end pt-1">
            <button type="submit"
              [disabled]="savingPayments() || paymentsForm.pristine"
              class="tfr-btn-primary px-5 py-2.5 text-[13px]">
              @if (savingPayments()) { Guardando&hellip; } @else { Guardar }
            </button>
          </div>
        </form>
      </section>
    }

  </div>
</main>
